/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import {
  getFirm, updateFirm, deleteFirm,
  addFirmCall, updateFirmCall, deleteFirmCall,
  addFirmTask, updateFirmTask, deleteFirmTask,
  convertFirmToDesignPartner, getRecord,
} from '../utils/api';
import { STATUS_OPTIONS } from './FirmPipeline';
import useBodyScrollLock from '../hooks/useBodyScrollLock';

const EMPLOYEE_RANGES = ['1-5', '6-10', '11-25', '26-50', '51-100', '100+'];
const CALL_TYPES = ['Phone Call', 'Email', 'Video Call', 'In-Person Meeting', 'Demo', 'LinkedIn Message', 'Other'];
const TASK_TYPES = ['Follow-Up Action', 'Demo Scheduling', 'Email Follow-Up', 'Other'];

const inp = {
  background: 'var(--bg-card)', border: '1px solid var(--border-med)',
  borderRadius: 'var(--radius-sm)', color: 'var(--text)',
  padding: '7px 10px', fontSize: 13,
  fontFamily: 'DM Sans, sans-serif', width: '100%', outline: 'none',
};

const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600,
  color: 'var(--text-muted)', textTransform: 'uppercase',
  letterSpacing: '0.07em', marginBottom: 4,
};

function Field({ label, children }) {
  return <div style={{ marginBottom: 14 }}><label style={lbl}>{label}</label>{children}</div>;
}

function SectionHeader({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10, marginTop: 4 }}>
      {children}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  try { return format(typeof d === 'string' ? parseISO(d) : d, 'MMM d, yyyy'); } catch { return d; }
}

function fmtDateTime(d) {
  if (!d) return '—';
  try { return format(typeof d === 'string' ? parseISO(d) : d, 'MMM d, yyyy h:mm a'); } catch { return d; }
}

const DEFAULT_FORM = {
  firm_name: '', website: '', location: '', employee_count_range: '',
  contact_name: '', contact_title: '', phone: '', email: '',
  status: 'Lead', last_contacted: '', next_action: '',
  pain_points: '', current_software: '', opportunity_notes: '',
};

export default function FirmContactCard({ firmId, onClose, onChanged, onDeleted, showToast, onOpenDesignPartner }) {
  useBodyScrollLock();
  const [firm,     setFirm]     = useState(null);
  const [calls,    setCalls]    = useState([]);
  const [tasks,    setTasks]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('details');
  const [form,     setForm]     = useState(DEFAULT_FORM);
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState('');
  const [converting, setConverting] = useState(false);
  const [designPartnerRecord, setDesignPartnerRecord] = useState(null);
  const [opening,  setOpening]  = useState(false);

  const [newCall, setNewCall] = useState({ call_date: '', call_type: CALL_TYPES[0], discussion_summary: '' });
  const [newTask, setNewTask] = useState({ task_type: TASK_TYPES[0], description: '', due_date: '' });
  const [addingCall, setAddingCall] = useState(false);
  const [addingTask, setAddingTask] = useState(false);

  const [callDrafts, setCallDrafts] = useState({});
  const [taskDrafts, setTaskDrafts] = useState({});
  const [callBusy,   setCallBusy]   = useState({});
  const [taskBusy,   setTaskBusy]   = useState({});

  const toast = showToast || (() => {});

  const load = async () => {
    setLoading(true);
    try {
      const data = await getFirm(firmId);
      const { calls: c, tasks: t, ...rest } = data;
      setFirm(rest);
      setCalls(c || []);
      setTasks(t || []);
      setForm({
        firm_name:            rest.firm_name            || '',
        website:               rest.website               || '',
        location:              rest.location              || '',
        employee_count_range:  rest.employee_count_range  || '',
        contact_name:          rest.contact_name          || '',
        contact_title:         rest.contact_title         || '',
        phone:                 rest.phone                 || '',
        email:                 rest.email                 || '',
        status:                rest.status                || 'Lead',
        last_contacted:        rest.last_contacted ? rest.last_contacted.split('T')[0] : '',
        next_action:           rest.next_action           || '',
        pain_points:           rest.pain_points           || '',
        current_software:      rest.current_software      || '',
        opportunity_notes:     rest.opportunity_notes     || '',
      });
    } catch {
      toast('Failed to load firm', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setTab('details'); setDesignPartnerRecord(null); load(); }, [firmId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setError('');
    if (!form.firm_name.trim()) {
      setError('Firm Name is required to save.');
      return;
    }
    setSaving(true);
    try {
      const updated = await updateFirm(firmId, { ...form, firm_name: form.firm_name.trim() });
      setFirm(prev => ({ ...prev, ...updated }));
      toast('✓ Saved');
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${firm.firm_name}"? This removes its call history and tasks too. This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deleteFirm(firmId);
      toast('Firm deleted');
      onDeleted?.();
    } catch {
      toast('Delete failed', 'error');
      setDeleting(false);
    }
  };

  // ── Convert to Design Partner ──────────────────────────────────────────────
  const handleConvert = async () => {
    if (!window.confirm(`Convert "${firm.firm_name}" to a Design Partner? This creates a new Design Partner Tracker record and keeps this Firm Pipeline record for history.`)) return;
    setConverting(true);
    try {
      const { firm: updatedFirm, designPartner } = await convertFirmToDesignPartner(firmId);
      setFirm(prev => ({ ...prev, ...updatedFirm }));
      setForm(f => ({ ...f, status: updatedFirm.status }));
      setDesignPartnerRecord(designPartner);
      toast('✓ Converted to Design Partner');
      onChanged?.();
    } catch (e) {
      toast(e.response?.data?.error || 'Conversion failed', 'error');
    } finally {
      setConverting(false);
    }
  };

  const handleOpenDesignPartner = async () => {
    if (designPartnerRecord) {
      onOpenDesignPartner?.(designPartnerRecord);
      onClose?.();
      return;
    }
    setOpening(true);
    try {
      const record = await getRecord(firm.design_partner_id);
      onOpenDesignPartner?.(record);
      onClose?.();
    } catch {
      toast('Failed to open Design Partner record', 'error');
    } finally {
      setOpening(false);
    }
  };

  // ── Call History ────────────────────────────────────────────────────────
  const handleAddCall = async (e) => {
    e.preventDefault();
    if (!newCall.call_date && !newCall.discussion_summary.trim()) return;
    setAddingCall(true);
    try {
      const call = await addFirmCall(firmId, newCall);
      setCalls(prev => [call, ...prev]);
      setNewCall({ call_date: '', call_type: CALL_TYPES[0], discussion_summary: '' });
    } catch {
      toast('Failed to add call entry', 'error');
    } finally {
      setAddingCall(false);
    }
  };

  const setCallDraftField = (callId, field, value) => {
    setCallDrafts(d => ({ ...d, [callId]: { ...d[callId], [field]: value } }));
  };

  const cancelCallDraft = (callId) => {
    setCallDrafts(d => { const nd = { ...d }; delete nd[callId]; return nd; });
  };

  const saveCallEntry = async (call) => {
    const draft = callDrafts[call.id];
    if (!draft || !Object.keys(draft).length) return;
    setCallBusy(b => ({ ...b, [call.id]: true }));
    try {
      const updated = await updateFirmCall(firmId, call.id, { ...call, ...draft });
      setCalls(prev => prev.map(c => c.id === call.id ? updated : c));
      cancelCallDraft(call.id);
      toast('✓ Saved');
    } catch {
      toast('Failed to update call entry', 'error');
    } finally {
      setCallBusy(b => { const nb = { ...b }; delete nb[call.id]; return nb; });
    }
  };

  const removeCall = async (callId) => {
    if (!window.confirm('Delete this call entry?')) return;
    setCallBusy(b => ({ ...b, [callId]: true }));
    try {
      await deleteFirmCall(firmId, callId);
      setCalls(prev => prev.filter(c => c.id !== callId));
      cancelCallDraft(callId);
    } catch {
      toast('Failed to delete call entry', 'error');
      setCallBusy(b => { const nb = { ...b }; delete nb[callId]; return nb; });
    }
  };

  // ── Tasks ───────────────────────────────────────────────────────────────
  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTask.description.trim()) return;
    setAddingTask(true);
    try {
      const task = await addFirmTask(firmId, { ...newTask, status: 'Open' });
      setTasks(prev => [task, ...prev]);
      setNewTask({ task_type: TASK_TYPES[0], description: '', due_date: '' });
      onChanged?.();
    } catch {
      toast('Failed to add task', 'error');
    } finally {
      setAddingTask(false);
    }
  };

  const setTaskDraftField = (taskId, field, value) => {
    setTaskDrafts(d => ({ ...d, [taskId]: { ...d[taskId], [field]: value } }));
  };

  const cancelTaskDraft = (taskId) => {
    setTaskDrafts(d => { const nd = { ...d }; delete nd[taskId]; return nd; });
  };

  const saveTaskEntry = async (task) => {
    const draft = taskDrafts[task.id];
    if (!draft || !Object.keys(draft).length) return;
    setTaskBusy(b => ({ ...b, [task.id]: true }));
    try {
      const updated = await updateFirmTask(firmId, task.id, { ...task, ...draft });
      setTasks(prev => prev.map(t => t.id === task.id ? updated : t));
      cancelTaskDraft(task.id);
      toast('✓ Saved');
      if ('status' in draft) onChanged?.();
    } catch {
      toast('Failed to update task', 'error');
    } finally {
      setTaskBusy(b => { const nb = { ...b }; delete nb[task.id]; return nb; });
    }
  };

  const removeTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    setTaskBusy(b => ({ ...b, [taskId]: true }));
    try {
      await deleteFirmTask(firmId, taskId);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      cancelTaskDraft(taskId);
      onChanged?.();
    } catch {
      toast('Failed to delete task', 'error');
      setTaskBusy(b => { const nb = { ...b }; delete nb[taskId]; return nb; });
    }
  };

  const openTaskCount = tasks.filter(t => t.status === 'Open').length;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)', zIndex: 200 }} />

      <div className="drawer-panel" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 580, maxWidth: '96vw',
        background: 'var(--bg-card2)',
        borderLeft: '1px solid var(--border-med)',
        zIndex: 201, display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.15)',
        animation: 'slideIn 0.2s ease',
      }}>
        <style>{`
          @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
          .fc:focus { border-color: var(--primary) !important; }
        `}</style>

        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600, fontSize: 15, color: 'var(--text)' }}>
              {loading ? 'Loading…' : (firm?.firm_name || 'Firm Contact Card')}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              {firm?.location || ''}{firm?.location ? ' · ' : ''}Firm Pipeline
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          {[
            { key: 'details', label: 'Details' },
            { key: 'calls',   label: `Call History${calls.length ? ` (${calls.length})` : ''}` },
            { key: 'tasks',   label: `Tasks${openTaskCount ? ` (${openTaskCount})` : ''}` },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              flex: 1, background: 'none', border: 'none',
              borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
              color: tab === t.key ? 'var(--primary)' : 'var(--text-muted)',
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: tab === t.key ? 600 : 400,
              fontSize: 13, padding: '10px', cursor: 'pointer', marginBottom: -1,
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="drawer-body" style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {loading ? (
            <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
          ) : tab === 'details' ? (
            <>
              {/* Pipeline status */}
              <div style={{ background: 'rgba(46,162,235,0.06)', border: '1px solid var(--primary-border)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 18 }}>
                <SectionHeader>◈ Pipeline Status</SectionHeader>
                <Field label="Status">
                  <select className="fc" value={form.status} onChange={e => set('status', e.target.value)} style={inp}>
                    {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                  <Field label="Last Contacted">
                    <input className="fc" type="date" value={form.last_contacted || ''} onChange={e => set('last_contacted', e.target.value)} style={inp} />
                  </Field>
                  <Field label="Next Action">
                    <input className="fc" type="text" value={form.next_action || ''} onChange={e => set('next_action', e.target.value)} style={inp} />
                  </Field>
                </div>
                {firm?.design_partner_id ? (
                  <div className="drawer-banner" style={{ marginTop: 8, padding: '8px 10px', background: 'var(--bg-card)', border: '1px dashed var(--border-med)', borderRadius: 'var(--radius-sm)', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <span style={{ color: '#10B981', fontWeight: 600 }}>✓ Design Partner Created</span>
                    <button onClick={handleOpenDesignPartner} disabled={opening} style={{
                      background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))',
                      border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff',
                      fontSize: 11, fontWeight: 600, padding: '5px 10px',
                      cursor: opening ? 'not-allowed' : 'pointer', opacity: opening ? 0.7 : 1, whiteSpace: 'nowrap',
                    }}>
                      {opening ? '…' : 'Open Design Partner →'}
                    </button>
                  </div>
                ) : (
                  <div className="drawer-banner" style={{ marginTop: 8, padding: '8px 10px', background: 'var(--bg-card)', border: '1px dashed var(--border-med)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <span>This firm can be converted to a Design Partner record at any time.</span>
                    <button onClick={handleConvert} disabled={converting} style={{
                      background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))',
                      border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff',
                      fontSize: 11, fontWeight: 600, padding: '5px 10px',
                      cursor: converting ? 'not-allowed' : 'pointer', opacity: converting ? 0.7 : 1, whiteSpace: 'nowrap',
                    }}>
                      {converting ? 'Converting…' : '→ Convert to Design Partner'}
                    </button>
                  </div>
                )}
              </div>

              {/* Firm Information */}
              <SectionHeader>Firm Information</SectionHeader>
              <Field label="Firm Name">
                <input className="fc" type="text" value={form.firm_name} onChange={e => set('firm_name', e.target.value)} style={inp} />
              </Field>
              <Field label="Website">
                <input className="fc" type="text" placeholder="https://…" value={form.website} onChange={e => set('website', e.target.value)} style={inp} />
              </Field>
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                <Field label="Location">
                  <input className="fc" type="text" placeholder="City, State" value={form.location} onChange={e => set('location', e.target.value)} style={inp} />
                </Field>
                <Field label="Number of Employees">
                  <select className="fc" value={form.employee_count_range} onChange={e => set('employee_count_range', e.target.value)} style={inp}>
                    <option value="">— none —</option>
                    {EMPLOYEE_RANGES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </Field>
              </div>

              {/* Primary Contact */}
              <SectionHeader>Primary Contact</SectionHeader>
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                <Field label="Name">
                  <input className="fc" type="text" value={form.contact_name} onChange={e => set('contact_name', e.target.value)} style={inp} />
                </Field>
                <Field label="Title">
                  <input className="fc" type="text" value={form.contact_title} onChange={e => set('contact_title', e.target.value)} style={inp} />
                </Field>
              </div>
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                <Field label="Phone">
                  <input className="fc" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} style={inp} />
                </Field>
                <Field label="Email">
                  <input className="fc" type="email" value={form.email} onChange={e => set('email', e.target.value)} style={inp} />
                </Field>
              </div>

              {/* Notes */}
              <SectionHeader>Notes</SectionHeader>
              <Field label="Pain Points">
                <textarea className="fc" value={form.pain_points} onChange={e => set('pain_points', e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} />
              </Field>
              <Field label="Current Software">
                <input className="fc" type="text" value={form.current_software} onChange={e => set('current_software', e.target.value)} style={inp} />
              </Field>
              <Field label="Opportunity Notes">
                <textarea className="fc" value={form.opportunity_notes} onChange={e => set('opportunity_notes', e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} />
              </Field>

              <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.8, marginTop: 8 }}>
                <div>Created: {firm?.created_at ? new Date(firm.created_at).toLocaleString() : '—'} by {firm?.created_by || '—'}</div>
                <div>Last updated: {firm?.last_updated_at ? new Date(firm.last_updated_at).toLocaleString() : '—'} by {firm?.last_updated_by || '—'}</div>
              </div>
            </>
          ) : tab === 'calls' ? (
            <>
              <SectionHeader>Log a Call</SectionHeader>
              <form onSubmit={handleAddCall} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 18 }}>
                <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                  <Field label="Date of Contact">
                    <input className="fc" type="date" value={newCall.call_date} onChange={e => setNewCall(c => ({ ...c, call_date: e.target.value }))} style={inp} />
                  </Field>
                  <Field label="Call Type">
                    <select className="fc" value={newCall.call_type} onChange={e => setNewCall(c => ({ ...c, call_type: e.target.value }))} style={inp}>
                      {CALL_TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Discussion Summary">
                  <textarea className="fc" value={newCall.discussion_summary} onChange={e => setNewCall(c => ({ ...c, discussion_summary: e.target.value }))} rows={3} style={{ ...inp, resize: 'vertical' }} />
                </Field>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" disabled={addingCall} style={{
                    background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))',
                    border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff',
                    fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 13,
                    padding: '7px 16px', cursor: addingCall ? 'not-allowed' : 'pointer', opacity: addingCall ? 0.7 : 1,
                  }}>
                    {addingCall ? 'Adding…' : '+ Add Entry'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewCall({ call_date: '', call_type: CALL_TYPES[0], discussion_summary: '' })}
                    disabled={addingCall}
                    style={{
                      background: 'var(--bg-card2)', border: '1px solid var(--border-med)',
                      borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)',
                      fontFamily: 'DM Sans, sans-serif', fontSize: 13,
                      padding: '7px 16px', cursor: addingCall ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>

              <SectionHeader>History</SectionHeader>
              {!calls.length ? (
                <div style={{ color: 'var(--text-dim)', fontSize: 13, padding: '8px 0' }}>No calls logged yet.</div>
              ) : (
                calls.map(c => {
                  const draft = callDrafts[c.id];
                  const dirty = !!draft && Object.keys(draft).length > 0;
                  const busy = !!callBusy[c.id];
                  const val = (field) => (draft && Object.prototype.hasOwnProperty.call(draft, field)) ? draft[field] : c[field];
                  return (
                    <div key={c.id} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, marginBottom: 10, background: 'var(--bg-card)' }}>
                      <div className="entry-row" style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                        <input
                          type="date" value={val('call_date') ? String(val('call_date')).split('T')[0] : ''}
                          onChange={e => setCallDraftField(c.id, 'call_date', e.target.value || null)}
                          style={{ ...inp, width: 150 }}
                        />
                        <select
                          value={val('call_type') || ''}
                          onChange={e => setCallDraftField(c.id, 'call_type', e.target.value)}
                          style={{ ...inp, width: 'auto', flex: 1 }}
                        >
                          {CALL_TYPES.map(t => <option key={t}>{t}</option>)}
                        </select>
                      </div>
                      <textarea
                        value={val('discussion_summary') || ''}
                        onChange={e => setCallDraftField(c.id, 'discussion_summary', e.target.value)}
                        rows={2} placeholder="Discussion summary…"
                        style={{ ...inp, resize: 'vertical' }}
                      />
                      <div style={{ fontSize: 10, color: 'var(--text-dim)', margin: '6px 0 10px' }}>Logged {fmtDateTime(c.created_at)} by {c.created_by || '—'}</div>
                      <div className="entry-actions" style={{ display: 'flex', gap: 8 }}>
                        <button className="entry-save" onClick={() => saveCallEntry(c)} disabled={!dirty || busy} style={{
                          flex: 1, background: dirty ? 'linear-gradient(135deg, var(--primary), var(--primary-dark))' : 'var(--bg-card2)',
                          border: 'none', borderRadius: 'var(--radius-sm)', color: dirty ? '#fff' : 'var(--text-dim)',
                          fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 12,
                          padding: '6px 12px', cursor: (dirty && !busy) ? 'pointer' : 'not-allowed', opacity: busy ? 0.7 : 1,
                        }}>
                          {busy ? '…' : '✓ Save'}
                        </button>
                        <button className="entry-delete" onClick={() => removeCall(c.id)} disabled={busy} style={{
                          background: 'var(--danger-bg)', border: '1px solid rgba(248,113,113,0.2)',
                          borderRadius: 'var(--radius-sm)', color: 'var(--danger)',
                          fontFamily: 'DM Sans, sans-serif', fontWeight: 500, fontSize: 12,
                          padding: '6px 12px', cursor: busy ? 'not-allowed' : 'pointer',
                        }}>
                          Delete
                        </button>
                        <button className="entry-cancel" onClick={() => cancelCallDraft(c.id)} disabled={!dirty || busy} style={{
                          background: 'var(--bg-card2)', border: '1px solid var(--border-med)',
                          borderRadius: 'var(--radius-sm)', color: dirty ? 'var(--text-muted)' : 'var(--text-dim)',
                          fontFamily: 'DM Sans, sans-serif', fontSize: 12,
                          padding: '6px 12px', cursor: (dirty && !busy) ? 'pointer' : 'not-allowed',
                        }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </>
          ) : (
            <>
              <SectionHeader>Add a Task</SectionHeader>
              <form onSubmit={handleAddTask} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 18 }}>
                <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                  <Field label="Task Type">
                    <select className="fc" value={newTask.task_type} onChange={e => setNewTask(t => ({ ...t, task_type: e.target.value }))} style={inp}>
                      {TASK_TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Due Date">
                    <input className="fc" type="date" value={newTask.due_date} onChange={e => setNewTask(t => ({ ...t, due_date: e.target.value }))} style={inp} />
                  </Field>
                </div>
                <Field label="Description">
                  <textarea className="fc" value={newTask.description} onChange={e => setNewTask(t => ({ ...t, description: e.target.value }))} rows={2} style={{ ...inp, resize: 'vertical' }} />
                </Field>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" disabled={addingTask || !newTask.description.trim()} style={{
                    background: newTask.description.trim() ? 'linear-gradient(135deg, var(--primary), var(--primary-dark))' : 'var(--bg-card2)',
                    border: 'none', borderRadius: 'var(--radius-sm)', color: newTask.description.trim() ? '#fff' : 'var(--text-dim)',
                    fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 13,
                    padding: '7px 16px', cursor: addingTask || !newTask.description.trim() ? 'not-allowed' : 'pointer',
                  }}>
                    {addingTask ? 'Adding…' : '+ Add Task'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewTask({ task_type: TASK_TYPES[0], description: '', due_date: '' })}
                    disabled={addingTask}
                    style={{
                      background: 'var(--bg-card2)', border: '1px solid var(--border-med)',
                      borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)',
                      fontFamily: 'DM Sans, sans-serif', fontSize: 13,
                      padding: '7px 16px', cursor: addingTask ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>

              <SectionHeader>Task Log</SectionHeader>
              {!tasks.length ? (
                <div style={{ color: 'var(--text-dim)', fontSize: 13, padding: '8px 0' }}>No tasks yet.</div>
              ) : (
                tasks.map(t => {
                  const draft = taskDrafts[t.id];
                  const dirty = !!draft && Object.keys(draft).length > 0;
                  const busy = !!taskBusy[t.id];
                  const val = (field) => (draft && Object.prototype.hasOwnProperty.call(draft, field)) ? draft[field] : t[field];
                  const isDone = val('status') === 'Done';
                  return (
                    <div key={t.id} style={{
                      border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12, marginBottom: 10,
                      background: 'var(--bg-card)', opacity: isDone ? 0.6 : 1,
                    }}>
                      <div className="entry-row" style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                        <label className="check-row" style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', flexShrink: 0 }}>
                          <input
                            type="checkbox" checked={isDone}
                            onChange={e => setTaskDraftField(t.id, 'status', e.target.checked ? 'Done' : 'Open')}
                            style={{ width: 15, height: 15, accentColor: 'var(--primary)' }}
                          />
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{isDone ? 'Done' : 'Open'}</span>
                        </label>
                        <select
                          value={val('task_type') || ''}
                          onChange={e => setTaskDraftField(t.id, 'task_type', e.target.value)}
                          style={{ ...inp, width: 'auto', flex: 1 }}
                        >
                          {TASK_TYPES.map(tt => <option key={tt}>{tt}</option>)}
                        </select>
                        <input
                          type="date" value={val('due_date') ? String(val('due_date')).split('T')[0] : ''}
                          onChange={e => setTaskDraftField(t.id, 'due_date', e.target.value || null)}
                          style={{ ...inp, width: 150 }}
                        />
                      </div>
                      <textarea
                        value={val('description') || ''}
                        onChange={e => setTaskDraftField(t.id, 'description', e.target.value)}
                        rows={2} placeholder="Task description…"
                        style={{ ...inp, resize: 'vertical' }}
                      />
                      {t.status === 'Done' && t.completed_at && (
                        <div style={{ fontSize: 10, color: 'var(--text-dim)', margin: '6px 0 10px' }}>Completed {fmtDate(t.completed_at)}</div>
                      )}
                      <div className="entry-actions" style={{ display: 'flex', gap: 8, marginTop: t.status === 'Done' && t.completed_at ? 0 : 10 }}>
                        <button className="entry-save" onClick={() => saveTaskEntry(t)} disabled={!dirty || busy} style={{
                          flex: 1, background: dirty ? 'linear-gradient(135deg, var(--primary), var(--primary-dark))' : 'var(--bg-card2)',
                          border: 'none', borderRadius: 'var(--radius-sm)', color: dirty ? '#fff' : 'var(--text-dim)',
                          fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 12,
                          padding: '6px 12px', cursor: (dirty && !busy) ? 'pointer' : 'not-allowed', opacity: busy ? 0.7 : 1,
                        }}>
                          {busy ? '…' : '✓ Save'}
                        </button>
                        <button className="entry-delete" onClick={() => removeTask(t.id)} disabled={busy} style={{
                          background: 'var(--danger-bg)', border: '1px solid rgba(248,113,113,0.2)',
                          borderRadius: 'var(--radius-sm)', color: 'var(--danger)',
                          fontFamily: 'DM Sans, sans-serif', fontWeight: 500, fontSize: 12,
                          padding: '6px 12px', cursor: busy ? 'not-allowed' : 'pointer',
                        }}>
                          Delete
                        </button>
                        <button className="entry-cancel" onClick={() => cancelTaskDraft(t.id)} disabled={!dirty || busy} style={{
                          background: 'var(--bg-card2)', border: '1px solid var(--border-med)',
                          borderRadius: 'var(--radius-sm)', color: dirty ? 'var(--text-muted)' : 'var(--text-dim)',
                          fontFamily: 'DM Sans, sans-serif', fontSize: 12,
                          padding: '6px 12px', cursor: (dirty && !busy) ? 'pointer' : 'not-allowed',
                        }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {tab === 'details' && !loading && (
          <div className="drawer-footer" style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', flexShrink: 0, background: 'var(--bg-card)' }}>
            {error && (
              <div style={{ background: 'var(--danger-bg)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 'var(--radius-sm)', color: 'var(--danger)', fontSize: 12, padding: '7px 12px', marginBottom: 10 }}>
                ⚠ {error}
              </div>
            )}
            <div className="drawer-actions" style={{ display: 'flex', gap: 8 }}>
              <button className="drawer-save" onClick={handleSave} disabled={saving} style={{
                flex: 1, background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))',
                border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff',
                fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 13, padding: '9px',
                cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
              }}>
                {saving ? 'Saving…' : '✓ Save'}
              </button>
              <button className="drawer-delete" onClick={handleDelete} disabled={deleting} style={{
                background: 'var(--danger-bg)', border: '1px solid rgba(248,113,113,0.2)',
                borderRadius: 'var(--radius-sm)', color: 'var(--danger)',
                fontFamily: 'DM Sans, sans-serif', fontWeight: 500, fontSize: 13, padding: '9px 16px', cursor: 'pointer',
              }}>
                {deleting ? '…' : 'Delete'}
              </button>
              <button className="drawer-cancel" onClick={onClose} style={{
                background: 'var(--bg-card2)', border: '1px solid var(--border-med)',
                borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)',
                fontFamily: 'DM Sans, sans-serif', fontSize: 13, padding: '9px 14px', cursor: 'pointer',
              }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
